/**
 * AttachmentTray
 *
 * The files you've attached, shown inside the chat bar above where you
 * type (the bar grows up to fit them) instead of chips floating over it.
 * Each file is a small card: a thumbnail (a real preview for images, a
 * format tile otherwise), the name, and its type and size — "PDF · 240 KB".
 * Uploading shows a spinner on the thumbnail; ready shows a check; failed
 * says so. Used by DwAgentChatBar for both its own uploads and a page
 * target's (a command's files).
 */
import React from 'react';
import {
  FiX, FiCheck, FiRefreshCw, FiAlertCircle,
  FiFileText, FiFile, FiImage, FiFolder, FiGrid,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';

export interface TrayItem {
  key: string;
  name: string;
  size?: number;
  status: 'uploading' | 'ready' | 'error';
  /** Object URL of the picked image, for a real thumbnail. */
  previewUrl?: string;
  error?: string;
}

interface Kind {
  label: string;
  Icon: IconType;
  /** Accent for the format tile — a muted tint, not a loud colour. */
  tone: 'doc' | 'sheet' | 'pdf' | 'image' | 'media' | 'archive' | 'code' | 'other';
}

const KINDS: Record<string, Kind> = {
  pdf: { label: 'PDF', Icon: FiFileText, tone: 'pdf' },
  doc: { label: 'Word document', Icon: FiFileText, tone: 'doc' },
  docx: { label: 'Word document', Icon: FiFileText, tone: 'doc' },
  txt: { label: 'Text', Icon: FiFileText, tone: 'doc' },
  rtf: { label: 'Text', Icon: FiFileText, tone: 'doc' },
  xls: { label: 'Excel spreadsheet', Icon: FiGrid, tone: 'sheet' },
  xlsx: { label: 'Excel spreadsheet', Icon: FiGrid, tone: 'sheet' },
  csv: { label: 'CSV', Icon: FiGrid, tone: 'sheet' },
  png: { label: 'Image', Icon: FiImage, tone: 'image' },
  jpg: { label: 'Image', Icon: FiImage, tone: 'image' },
  jpeg: { label: 'Image', Icon: FiImage, tone: 'image' },
  gif: { label: 'Image', Icon: FiImage, tone: 'image' },
  webp: { label: 'Image', Icon: FiImage, tone: 'image' },
  heic: { label: 'Image', Icon: FiImage, tone: 'image' },
  svg: { label: 'Image', Icon: FiImage, tone: 'image' },
  mp4: { label: 'Video', Icon: FiFile, tone: 'media' },
  mov: { label: 'Video', Icon: FiFile, tone: 'media' },
  mp3: { label: 'Audio', Icon: FiFile, tone: 'media' },
  wav: { label: 'Audio', Icon: FiFile, tone: 'media' },
  m4a: { label: 'Audio', Icon: FiFile, tone: 'media' },
  zip: { label: 'ZIP archive', Icon: FiFolder, tone: 'archive' },
  json: { label: 'JSON', Icon: FiFile, tone: 'code' },
  xml: { label: 'XML', Icon: FiFile, tone: 'code' },
};

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

export function isImageName(name: string): boolean {
  return KINDS[extensionOf(name)]?.tone === 'image';
}

function kindOf(name: string): Kind & { ext: string } {
  const ext = extensionOf(name);
  const kind = KINDS[ext] ?? { label: ext ? `${ext.toUpperCase()} file` : 'File', Icon: FiFile, tone: 'other' as const };
  return { ...kind, ext };
}

function formatSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  items: TrayItem[];
  onRemove: (key: string) => void;
}

const AttachmentTray: React.FC<Props> = ({ items, onRemove }) => {
  if (items.length === 0) return null;
  return (
    <ul className="rql-pcb__tray" aria-label="Attached files" role="list">
      {items.map((item) => {
        const kind = kindOf(item.name);
        const size = formatSize(item.size);
        const meta =
          item.status === 'uploading' ? 'Uploading…'
          : item.status === 'error' ? (item.error || 'Upload failed')
          : [kind.label, size].filter(Boolean).join(' · ');
        return (
          <li
            key={item.key}
            className={`rql-pcb__file rql-pcb__file--${item.status}`}
            title={item.status === 'ready' ? `${item.name} — ready to send` : item.name}
          >
            <span className={`rql-pcb__file-thumb rql-pcb__file-thumb--${kind.tone}`} aria-hidden="true">
              {item.previewUrl
                ? <img src={item.previewUrl} alt="" />
                : (
                  <>
                    <kind.Icon size={15} />
                    {kind.ext && <span className="rql-pcb__file-ext">{kind.ext.slice(0, 4)}</span>}
                  </>
                )}
              {item.status === 'uploading' && (
                <span className="rql-pcb__file-veil"><FiRefreshCw size={13} className="rql-pcb__attachment-spin" /></span>
              )}
              {item.status === 'ready' && (
                <span className="rql-pcb__file-ok"><FiCheck size={9} /></span>
              )}
            </span>
            <span className="rql-pcb__file-text">
              <span className="rql-pcb__file-name">{item.name}</span>
              <span className={`rql-pcb__file-meta${item.status === 'error' ? ' rql-pcb__file-meta--error' : ''}`}>
                {item.status === 'error' && <FiAlertCircle size={10} aria-hidden="true" />}
                {meta}
              </span>
            </span>
            {item.status !== 'uploading' && (
              <button
                type="button"
                className="rql-pcb__file-remove"
                onClick={() => onRemove(item.key)}
                aria-label={`Remove ${item.name}`}
                title="Remove"
              >
                <FiX size={12} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default AttachmentTray;
